import React, { useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Message,
  Modal,
  Text,
  toast,
} from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import type { MarkJetAssignmentsReturnRequestedResponse } from 'api/admin/jet/markJetAssignmentsReturnRequested'
import { useMarkJetAssignmentsReturnRequestedMutation } from 'queries/jet/useMarkJetAssignmentsReturnRequestedMutation'

// Lets ops bulk-mark the AIDs WorkWhile asked Advantage to return, so billing
// can tell a requested return from an Advantage-initiated removal.
export const JetAssignmentsReturnRequestedUpload = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] =
    useState<MarkJetAssignmentsReturnRequestedResponse | null>(null)

  const { mutate, isPending, error, reset } =
    useMarkJetAssignmentsReturnRequestedMutation()

  const close = () => {
    setIsOpen(false)
    setFile(null)
    setResult(null)
    reset()
  }

  const handleSubmit = () => {
    if (!file) {
      toast.error('Select a CSV file')
      return
    }
    mutate(file, {
      onSuccess: (data) => {
        setResult(data)
        toast.success(
          `Marked ${data.updatedCount} assignment(s) as return-requested`
        )
      },
      onError: (err) => {
        toast.error(parseErrorMessage(err) || 'Failed to mark assignments')
      },
    })
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        Upload return-requested CSV
      </Button>
      <Modal
        open={isOpen}
        onClose={close}
        position="top"
        loading={isPending}
        showCancel
        customCta={
          <Flex>
            <Button variant="text" onClick={close}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Upload
            </Button>
          </Flex>
        }
      >
        <Box>
          <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
            Mark return-requested AIDs
          </Heading>
          <Text mt={2} fontSize={1} color="lightText">
            Upload a CSV of assignment ids (first column, header optional). Each
            matching assignment is flagged as returned at WorkWhile&apos;s
            request.
          </Text>
          <Field
            label="CSV file"
            // @ts-expect-error - mt is not a valid prop for Field
            mt={3}
          >
            <input
              type="file"
              accept="text/csv,.csv"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>
          {error ? (
            <Message variant="error" mt={3} borderRadius="small">
              {parseErrorMessage(error)}
            </Message>
          ) : null}
          {result ? (
            <Message variant="success" mt={3} borderRadius="small">
              {`Marked ${result.updatedCount} assignment(s). `}
              {result.notFoundCount > 0
                ? `Not found: ${result.notFoundAssignmentIds.join(', ')}`
                : 'All ids matched.'}
            </Message>
          ) : null}
        </Box>
      </Modal>
    </>
  )
}
